import type { AuthFormOutletContext } from "@v2/features/account/common";

import * as React from "react";

import { Alert } from "@replohq/lattice/blocks/alert/Alert";
import { LabeledControl } from "@replohq/lattice/blocks/labeled-control/LabeledControl";
import { Button } from "@replohq/lattice/components/button/Button";
import { Input } from "@replohq/lattice/components/input/Input";
import { cn } from "@replohq/lattice/utils/cn";
import { DURATION, EASING } from "@replohq/lattice/utils/motion";
import {
  authButtonClassName,
  authFieldClassName,
} from "@v2/features/account/authFieldStyles";
import { AuthMethodDivider } from "@v2/features/account/AuthMethodDivider";
import GoogleAuth from "@v2/features/account/GoogleAuth";
import {
  getCampaignSourceFromParams,
  hasShopifyInstallIntent,
} from "@v2/features/account/signupFunnelUtils";
import { getCampaignEventProperties } from "@v2/features/agent-beta/campaignCodes";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { useTheme } from "@v2/hooks/useTheme";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { handleFormEnter } from "replo-utils/react/handle-form-enter";
import { getChatSignInTitle } from "schemas/actionLinkRoutes";

import { getMarketingWorkflow } from "./marketingWorkflow";
import { TermsAndPrivacy } from "./TermsAndPrivacy";
import { useSignupForm } from "./useSignupForm";

const agentTermsClassName = "[&>div]:text-muted [&_a]:text-default";

const SignupForm = () => {
  const { effectiveMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const shouldReduceMotion = useReducedMotion();
  const shouldShowShopifyLoginInlineAlert =
    searchParams.get("error") === "login_in_to_associate_shopify";

  const { defaultProjectType, setDefaultProjectType } =
    useOutletContext<AuthFormOutletContext>();
  const logEvent = useLogAnalytics();
  const campaignSource = getCampaignSourceFromParams(searchParams);

  // eslint-disable-next-line replo/no-use-effect -- Fire the viewed event once when the signup screen mounts.
  React.useEffect(() => {
    logEvent(
      "auth.signup.viewed",
      getCampaignEventProperties({ source: campaignSource }),
    );
  }, [logEvent, campaignSource]);

  const isWorkflowSignup = getMarketingWorkflow(searchParams) !== null;
  const isShopifyInstall = hasShopifyInstallIntent(searchParams);
  const isAgentSignup = defaultProjectType === "agent";
  // A chat sent the person here (its action link or its Connect window): say which, and what for.
  const chatSignInTitle = getChatSignInTitle(searchParams.get("next"));
  let headerCopy: {
    title: React.ReactNode;
    description: React.ReactNode;
    isLeftAligned?: boolean;
  };
  if (chatSignInTitle) {
    headerCopy = {
      title: chatSignInTitle,
      description: "New to Replo? The same form creates your account.",
      isLeftAligned: false,
    };
  } else if (isWorkflowSignup) {
    headerCopy = {
      title: "Create your account",
      description: null,
      isLeftAligned: true,
    };
  } else if (isAgentSignup) {
    headerCopy = {
      title: (
        <>
          Welcome to <span className="font-light italic">Replo</span>
        </>
      ),
      description: null,
      isLeftAligned: false,
    };
  } else if (isShopifyInstall) {
    // Shopify App Store installs can't switch to the Agent funnel, so no beta pitch.
    headerCopy = {
      title: "Build pages for Shopify",
      description: "Design visually. Publish to your existing theme.",
    };
  } else {
    headerCopy = {
      title: "Build directly in Shopify Themes",
      description: (
        <>
          Want to try the new version of Replo? Click here to opt-in to
          Replo&apos;s{" "}
          <button
            type="button"
            onClick={() => setDefaultProjectType("agent")}
            className="cursor-pointer text-primary hover:underline"
          >
            newest beta
          </button>
          .
        </>
      ),
      isLeftAligned: true,
    };
  }

  const {
    emailRegisterProps,
    passwordRegisterProps,
    handleSubmit,
    onSubmit,
    onGoogleSubmit,
    emailError,
    passwordError,
    isLoadingCreateAccount,
    buttonShouldBeDisabled,
    displayErrorMessage,
  } = useSignupForm({ defaultProjectType });

  const removeSearchParam = () => {
    setSearchParams((previous) => {
      const newParams = new URLSearchParams(previous);
      newParams.delete("error");
      return newParams;
    });
  };

  return (
    <form
      onKeyDown={handleFormEnter}
      className="no-scrollbar"
      onSubmit={(data) => {
        if (shouldShowShopifyLoginInlineAlert) {
          removeSearchParam();
        }
        void handleSubmit(onSubmit)(data);
      }}
    >
      <div
        className={cn(
          "flex flex-col justify-center",
          isWorkflowSignup && "hidden",
          !isWorkflowSignup && "mb-8",
          headerCopy.isLeftAligned ? "items-start" : "items-center text-center",
        )}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={defaultProjectType}
            initial={shouldReduceMotion ? false : { opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: -4 }}
            transition={{
              duration: shouldReduceMotion ? 0 : DURATION.standard,
              ease: EASING.outQuint,
            }}
            className={cn(
              "flex flex-col gap-4",
              headerCopy.isLeftAligned ? "items-start" : "items-center",
            )}
          >
            {isWorkflowSignup ? (
              <h2 className="w-full text-left font-sans text-lg font-semibold text-default">
                {headerCopy.title}
              </h2>
            ) : (
              <h1
                className={cn(
                  "font-editorial text-3xl font-bold text-default",
                  headerCopy.isLeftAligned && "w-full text-left",
                )}
              >
                {headerCopy.title}
              </h1>
            )}
            {headerCopy.description && (
              <p
                className={cn(
                  "text-sm text-muted",
                  headerCopy.isLeftAligned
                    ? "w-full text-left"
                    : "text-balance",
                )}
              >
                {headerCopy.description}
              </p>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
      {(displayErrorMessage || shouldShowShopifyLoginInlineAlert) && (
        <Alert
          variant={
            shouldShowShopifyLoginInlineAlert && !displayErrorMessage
              ? "warning"
              : "error"
          }
          UNSAFE_className="mb-2"
          description={
            displayErrorMessage ??
            "Please create or log in to your Replo account to continue to Replo."
          }
        />
      )}
      <div className="flex w-full flex-col gap-4">
        <LabeledControl label="Email" size="base" id="email" error={emailError}>
          <Input
            id="email"
            data-enter-navigation=""
            enterKeyHint="next"
            UNSAFE_className={authFieldClassName}
            invalid={Boolean(emailError)}
            autoComplete="email"
            placeholder="you@company.com"
            {...emailRegisterProps}
            type="email"
            autoFocus
            size="lg"
          />
        </LabeledControl>
        <LabeledControl
          label="Password"
          size="base"
          id="password"
          error={passwordError}
        >
          <Input
            id="password"
            data-enter-navigation=""
            enterKeyHint="go"
            UNSAFE_className={authFieldClassName}
            invalid={Boolean(passwordError)}
            autoComplete="new-password"
            placeholder={
              isWorkflowSignup ? "At least 8 characters" : "••••••••••••"
            }
            {...passwordRegisterProps}
            type="password"
            size="lg"
          />
        </LabeledControl>
      </div>
      <Button
        className="w-full"
        UNSAFE_className={cn(
          isWorkflowSignup ? "mt-4" : "mt-6",
          authButtonClassName,
        )}
        variant="primary"
        size="lg"
        type="submit"
        loading={isLoadingCreateAccount}
        disabled={buttonShouldBeDisabled}
      >
        Continue
      </Button>
      {isWorkflowSignup ? (
        <div className="mt-4 flex w-full flex-col gap-4">
          <AuthMethodDivider />
          <GoogleAuth
            handleGoogleCredentials={onGoogleSubmit}
            effectiveMode={effectiveMode}
          />
          <div className={agentTermsClassName}>
            <TermsAndPrivacy subdued />
          </div>
        </div>
      ) : (
        <>
          <div className="my-4">
            <AuthMethodDivider />
          </div>
          <GoogleAuth
            handleGoogleCredentials={onGoogleSubmit}
            effectiveMode={effectiveMode}
          />
        </>
      )}
    </form>
  );
};

export default SignupForm;
